import videos from "constants/videos";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import VenoBox from "venobox";
import styles from "./single-video.module.css";

export default function SingleVideo() {
  const { id } = useParams();

  // Match by stable slug first; fall back to numeric id so older
  // numeric links (e.g. /videos/146) keep working.
  const video =
    videos.find((video) => video.slug === id) ||
    videos.find((video) => video.id === Number(id));

  useEffect(() => {
    if (!video) return;
    new VenoBox({
      selector: ".my-video-links",
      maxWidth: "85%",
      onPostOpen() {
        document.querySelector(".vbox-container").style.overflowY = "hidden";
      },
    });
  }, [video]);

  // An uploaded PDF is rendered as PNG page images (tablet-safe). PDFs are
  // converted to PNG at build time by scripts/convert-pdfs.mjs.
  const hasPdf = video && video.pdfImages && video.pdfImages.length > 0;

  if (!video) {
    return (
      <div className="bg-gray-200 min-h-screen py-16">
        <div className="max-w-4xl mx-auto bg-white p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Video not found</h1>
          <p className="text-gray-600 mb-6">
            This training video doesn't exist or may have moved.
          </p>
          <Link
            to="/"
            className="inline-block bg-primary text-white rounded px-6 py-3 font-bold"
          >
            View All Training Videos
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{video.title}</title>
      </Helmet>
      <div className="bg-gray-200 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Video — hidden for document-only (PDF) entries */}
          {hasPdf || (video.videoUrl === "" && video.videoSubtitleUrl === "") ? null : (
            <div className="mb-4">
              <div className="relative">
                <img src={video.thumbnailUrl} alt="" />
                <a
                  data-autoplay="true"
                  data-vbtype="video"
                  href={video.videoUrl}
                  aria-label={`Play video: ${video.title}`}
                  className="my-video-links absolute top-0 bg-[rgba(17,24,39,0.5)] h-full w-full flex justify-center items-center"
                >
                  <div className={styles.button} />
                </a>
              </div>
              <div className="bg-white p-4 font-bold flex flex-wrap gap-3 text-sm">
                <a
                  className="my-video-links bg-primary hover:bg-primary-600 transition-colors duration-200 text-white rounded px-4 py-3 text-center"
                  data-autoplay="true"
                  data-vbtype="video"
                  href={video.videoUrl}
                >
                  Watch Now
                </a>
                {video.videoSubtitleUrl ? (
                  <a
                    lang="es"
                    className="my-video-links bg-red-700 hover:bg-red-800 transition-colors duration-200 text-white rounded px-4 py-3 text-center"
                    data-autoplay="true"
                    data-vbtype="video"
                    href={video.videoSubtitleUrl}
                  >
                    Ver Con Subtitulos En Español
                  </a>
                ) : null}

                <Link
                  to="/"
                  className="bg-gray-700 hover:bg-gray-800 transition-colors duration-200 text-white rounded px-4 py-3 text-center"
                >
                  View All Training Videos
                </Link>
              </div>
            </div>
          )}
          {/* Transcript */}
          {video.transcript
            ? video.transcript.map((t, index) => {
                return (
                  <div key={index} className="bg-white p-4 text-gray-800 text-lg">
                    <img
                      src={t}
                      alt={`${video.title} Transcript`}
                      loading="lazy"
                      className="w-full"
                    />
                  </div>
                );
              })
            : ""}

          {/* PDF document, rendered as PNG page images (tablet-safe) */}
          {hasPdf
            ? video.pdfImages.map((page, index) => (
                <div key={index} className="bg-white p-4 text-gray-800 text-lg">
                  <img
                    src={page}
                    alt={`${video.title} — page ${index + 1}`}
                    loading="lazy"
                    className="w-full"
                  />
                </div>
              ))
            : null}

          {/* Back to gallery for a document-only entry */}
          {hasPdf ? (
            <div className="bg-white p-4 font-bold flex text-sm">
              <Link
                to="/"
                className="bg-gray-700 hover:bg-gray-800 transition-colors duration-200 text-white rounded px-4 py-3 text-center"
              >
                View All Training Videos
              </Link>
            </div>
          ) : null}

        </div>
      </div>
    </>
  );
}
